import { Link } from "react-router-dom";
import { useContent } from "@/contexts/ContentContext";
import SizeCarousel from "@/components/SizeCarousel";
import { Check, ArrowRight } from "lucide-react";
import useSEO from "@/hooks/useSEO";

const SPECS = [
  { label: "Material", value: "Polypropylene over Fiberglass" },
  { label: "UV Protection", value: "Premium UV Inhibitor" },
  { label: "Origin", value: "Made in USA" },
  { label: "Rust Resistance", value: "Zero — non-metallic" },
  { label: "Application", value: "Marine pilings, docks" },
  { label: "Sizes Available", value: "10″ / 12″ / 14″" },
];

const SIZES = [
  { size: "10", desc: "For smaller pilings on residential docks and slips." },
  { size: "12", desc: "The most popular size — fits standard marina pilings." },
  { size: "14", desc: "Heavy-duty applications and oversized pilings." },
];

const ADVANTAGES = [
  "Minimizes Pile Wear.",
  "Withstands harsh marine conditions.",
  "Never rusts. Never corrodes. Non-metallic by design.",
  "UV-protected polypropylene.",
  "Fiberglass core provides structural rigidity that lasts.",
];

export default function Product() {
  const { content } = useContent();
  useSEO({
    title: "Pile Glide Pro — Marine Pile Hoop Specs & Sizes | Secure Stay Marine",
    description: "Pile Glide Pro specs: USA-made marine pile hoop in polypropylene & fiberglass, UV-protected. Sizes 10\", 12\", 14\". Minimizes wear, never rusts.",
    path: "/product",
  });

  return (
    <div data-testid="product-page">
      {/* INTRO */}
      <section className="border-b-2 border-[#091526]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-16 md:py-24">
          <h1 className="font-display text-5xl sm:text-6xl md:text-7xl lg:text-8xl uppercase leading-[0.95] mb-10 max-w-5xl">
            The Pile Glide Pro
          </h1>
          <p className="text-base md:text-lg max-w-3xl text-[#091526]/80 leading-relaxed">
            {content.product_intro}
          </p>
        </div>
      </section>

      {/* ADVANTAGE */}
      <section className="border-b-2 border-[#091526] bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-16 md:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="border-2 border-[#091526] bg-[#F4F4F5]">
              <img
                src="/images/stack-new.png"
                alt="Pile Glide Pro close-up"
                className="w-full h-[480px] object-cover"
                data-testid="product-detail-image"
              />
            </div>
          </div>
          <div className="lg:col-span-7 flex flex-col justify-center">
            <h2 className="font-display text-4xl md:text-5xl uppercase leading-none mb-8">
              The Pile Glide Pro Advantage
            </h2>
            <ul className="space-y-5">
              {ADVANTAGES.map((b) => (
                <li key={b} className="flex gap-4 items-start">
                  <div className="mt-1 flex-shrink-0 w-6 h-6 bg-[#1E5BB8] border-2 border-[#091526] flex items-center justify-center">
                    <Check size={14} strokeWidth={3} className="text-white" />
                  </div>
                  <span className="text-base text-[#091526]/85 leading-relaxed">{b}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* SIZES — matches Home */}
      <section className="border-b-2 border-[#091526]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-20 md:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <h2 className="font-display text-4xl sm:text-5xl md:text-6xl uppercase leading-none mb-6">
              Pile Guide Attachments
            </h2>
            <p className="text-base text-[#091526]/80 mb-6 leading-relaxed">{content.sizes_note}</p>

            <div className="bg-[#1E5BB8] text-white border-2 border-[#091526] px-5 py-4 mb-8">
              <p className="font-mono text-sm leading-relaxed">
                {content.sizes_fit_note || `Select a Pile Glide Pro at least 1" larger than your Pile's diameter for proper fit.`}
              </p>
            </div>

            <div className="border-2 border-[#091526]">
              {SIZES.map((s, i) => (
                <div
                  key={s.size}
                  className={`px-6 py-5 ${i < SIZES.length - 1 ? "border-b-2 border-[#091526]" : ""}`}
                  data-testid={`product-size-row-${s.size}`}
                >
                  <div className="flex justify-between items-center">
                    <span className="font-display text-3xl">{s.size}″</span>
                    <Link
                      to="/contact"
                      className="font-mono text-xs uppercase tracking-[0.2em] font-bold text-[#1E5BB8] hover:text-[#091526]"
                    >
                      Inquire →
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-7">
            <SizeCarousel testIdPrefix="product-size-carousel" />
          </div>
        </div>
      </section>

      {/* SPECS TABLE */}
      <section className="border-b-2 border-[#091526] bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-20 md:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <h2 className="font-display text-4xl md:text-5xl uppercase leading-none mb-6">
              Technical Spec.
            </h2>
            <p className="text-sm text-[#091526]/75 leading-relaxed max-w-md mb-8">
              Engineered with materials chosen for one purpose — to secure your floating
              dock and increase piling life.
            </p>
            <div className="h-28 w-48 border-2 border-[#091526] bg-white flex items-center justify-center p-2">
              <img
                src="/images/logo.png"
                alt="Pile Glide Pro"
                className="max-h-full max-w-full object-contain"
              />
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="border-2 border-[#091526]">
              {SPECS.map((row, i) => (
                <div
                  key={row.label}
                  className={`grid grid-cols-2 ${i < SPECS.length - 1 ? "border-b-2 border-[#091526]" : ""}`}
                >
                  <div className="px-6 py-4 font-mono text-xs uppercase tracking-[0.2em] font-bold border-r-2 border-[#091526] bg-[#F4F4F5]">
                    {row.label}
                  </div>
                  <div className="px-6 py-4 text-sm">{row.value}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* PRODUCT GALLERY */}
      <section className="border-b-2 border-[#091526]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-20 md:py-28">
          <div className="flex flex-col md:flex-row gap-6 items-stretch">
            <div className="flex-1 border-2 border-[#091526] bg-[#F4F4F5] overflow-hidden">
              <img
                src="/images/product-1.jpg"
                alt="Pile Glide Pro product shot"
                className="w-full h-[480px] md:h-[560px] object-cover object-center"
              />
            </div>
            <div className="md:w-[388px] md:flex-shrink-0 border-2 border-[#091526] bg-[#F4F4F5] overflow-hidden">
              <img
                src="/images/stack-row.png"
                alt="Pile Glide Pro stacked row"
                className="w-full h-[480px] md:h-[560px] object-cover object-center"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#091526] text-[#F4F4F5]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-20 md:py-28 text-center">
          <h2 className="font-display text-5xl sm:text-6xl md:text-7xl uppercase leading-none mb-8">
            Ready to protect your dock?
          </h2>
          <Link
            to="/contact"
            data-testid="product-cta-button"
            className="inline-flex items-center gap-3 bg-[#1E5BB8] text-white font-bold uppercase text-sm tracking-[0.2em] px-8 py-5 border-2 border-[#F4F4F5] hover:translate-x-[-3px] hover:translate-y-[-3px] transition-transform"
          >
            Request a Quote <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </div>
  );
}
